import React, { useMemo } from 'react';
import { useFlowBuilderStore } from '../../../stores/flowBuilderStore';
import { Input } from '../../ui';

interface MoveDealStageConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
}

type SelectOption = {
  value: string;
  label: string;
};

function normalizeOptions(rawItems: any): SelectOption[] {
  if (!rawItems) {
    return [];
  }

  if (Array.isArray(rawItems)) {
    return rawItems
      .map((item: any) => {
        const value = String(item?.ID ?? item?.VALUE ?? item?.value ?? item?.id ?? '').trim();
        const label = String(item?.VALUE ?? item?.NAME ?? item?.label ?? item?.TITLE ?? value).trim();
        return { value, label };
      })
      .filter((item) => item.value);
  }

  if (typeof rawItems === 'object') {
    return Object.entries(rawItems)
      .map(([key, item]: [string, any]) => {
        if (item && typeof item === 'object') {
          const value = String(item?.ID ?? item?.VALUE ?? item?.value ?? item?.id ?? key).trim();
          const label = String(item?.VALUE ?? item?.NAME ?? item?.label ?? item?.TITLE ?? value).trim();
          return { value, label };
        }

        return {
          value: String(key).trim(),
          label: String(item ?? key).trim(),
        };
      })
      .filter((item) => item.value);
  }

  return [];
}

function extractPipelineIdFromEntity(entityId: string): string {
  const normalized = String(entityId || '').trim().toUpperCase();
  const match = normalized.match(/^DEAL_STAGE(?:_(\d+))?$/);
  if (!match) {
    return '';
  }

  return match[1] ? String(match[1]) : '0';
}

export const MoveDealStageConfig: React.FC<MoveDealStageConfigProps> = ({
  config,
  onChange,
}) => {
  const { bitrixFields, nodes, edges, selectedNode } = useFlowBuilderStore();

  const targetEntity: 'deal' | 'lead' = config.targetEntity === 'lead' ? 'lead' : 'deal';
  const pipelineId = String(config.pipelineId || 'current').trim() || 'current';
  const stageId = String(config.stageId || '').trim();

  const dealCategories = Array.isArray(bitrixFields?.dealCategories)
    ? bitrixFields.dealCategories
    : [];

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const category of dealCategories) {
      const id = String(category?.ID ?? '').trim();
      if (!id) {
        continue;
      }

      const name = String(category?.NAME || `Pipeline ${id}`).trim();
      map.set(id, name || `Pipeline ${id}`);
    }

    if (!map.has('0')) {
      map.set('0', 'Pipeline Padrão');
    }

    return map;
  }, [dealCategories]);

  const stageOptions = useMemo(() => {
    const crmStatusByEntity = (bitrixFields?.crmStatusByEntity || {}) as Record<string, any>;
    const includeAllPipelines = pipelineId === 'current' || pipelineId === 'all';

    const options: SelectOption[] = [];
    const seen = new Set<string>();

    for (const [entityId, rawItems] of Object.entries(crmStatusByEntity)) {
      const normalizedEntityId = String(entityId || '').trim().toUpperCase();
      if (!normalizedEntityId.startsWith('DEAL_STAGE')) {
        continue;
      }

      const entityPipelineId = extractPipelineIdFromEntity(normalizedEntityId);
      if (!entityPipelineId) {
        continue;
      }

      if (!includeAllPipelines && entityPipelineId !== pipelineId) {
        continue;
      }

      const items = normalizeOptions(rawItems);
      const pipelineName = categoryNameById.get(entityPipelineId) || `Pipeline ${entityPipelineId}`;

      for (const item of items) {
        const dedupeKey = `${entityPipelineId}:${item.value}`;
        if (seen.has(dedupeKey)) {
          continue;
        }
        seen.add(dedupeKey);

        options.push({
          value: item.value,
          label: includeAllPipelines ? `${pipelineName}: ${item.label}` : item.label,
        });
      }
    }

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [bitrixFields?.crmStatusByEntity, categoryNameById, pipelineId]);

  // Status de lead (Bitrix crm.status.list ENTITY_ID = 'STATUS').
  const leadStatusOptions = useMemo(() => {
    const crmStatusByEntity = (bitrixFields?.crmStatusByEntity || {}) as Record<string, any>;
    const raw = crmStatusByEntity['STATUS'] ?? crmStatusByEntity['LEAD_STATUS'];
    return normalizeOptions(raw).sort((a, b) => a.label.localeCompare(b.label));
  }, [bitrixFields?.crmStatusByEntity]);

  const nodesInCurrentPath = useMemo(() => {
    const selectedNodeId = selectedNode?.id;
    if (!selectedNodeId) {
      return null;
    }

    const incomingMap = new Map<string, string[]>();

    for (const edge of edges) {
      const sourceId = String(edge.source || '').trim();
      const targetId = String(edge.target || '').trim();
      if (!sourceId || !targetId) {
        continue;
      }
      if (sourceId === '__instruction_note__' || targetId === '__instruction_note__') {
        continue;
      }

      const existing = incomingMap.get(targetId) || [];
      existing.push(sourceId);
      incomingMap.set(targetId, existing);
    }

    const visited = new Set<string>();
    const stack: string[] = [selectedNodeId];

    while (stack.length > 0) {
      const currentNodeId = stack.pop() as string;
      if (visited.has(currentNodeId)) {
        continue;
      }

      visited.add(currentNodeId);
      const parents = incomingMap.get(currentNodeId) || [];
      for (const parentId of parents) {
        if (!visited.has(parentId)) {
          stack.push(parentId);
        }
      }
    }

    return visited;
  }, [edges, selectedNode?.id]);

  const dealIdVariableOptions = useMemo(() => {
    const selectedNodeId = selectedNode?.id;
    if (!selectedNodeId || !nodesInCurrentPath) {
      return [] as Array<{ value: string; label: string }>;
    }

    const incomingMap = new Map<string, string[]>();
    for (const edge of edges) {
      const sourceId = String(edge.source || '').trim();
      const targetId = String(edge.target || '').trim();
      if (!sourceId || !targetId) {
        continue;
      }
      const existing = incomingMap.get(targetId) || [];
      existing.push(sourceId);
      incomingMap.set(targetId, existing);
    }

    const distanceToSelected = new Map<string, number>();
    const queue: Array<{ id: string; distance: number }> = [{ id: selectedNodeId, distance: 0 }];
    while (queue.length > 0) {
      const current = queue.shift() as { id: string; distance: number };
      const previousDistance = distanceToSelected.get(current.id);
      if (previousDistance !== undefined && previousDistance <= current.distance) {
        continue;
      }
      distanceToSelected.set(current.id, current.distance);

      const parents = incomingMap.get(current.id) || [];
      for (const parentId of parents) {
        queue.push({ id: parentId, distance: current.distance + 1 });
      }
    }

    const options: Array<{ value: string; label: string; score: number }> = [];
    const seen = new Set<string>();

    for (const node of nodes) {
      if (!nodesInCurrentPath.has(node.id) || node.id === selectedNodeId) {
        continue;
      }

      const data = node.data as any;
      if (data?.type !== 'SEARCH_CONTACT') {
        continue;
      }

      const searchIn = data?.config?.searchIn === 'deal' ? 'deal' : 'contact_company';
      if (searchIn !== 'deal') {
        continue;
      }

      const nodeLabel = String(data?.label || 'Buscar Cliente').trim();
      const distance = Number(distanceToSelected.get(node.id) ?? 9999);
      const score = Number.isFinite(distance) ? distance : 9999;

      const variableCandidates = ['found_entity_id', 'found_deal_id'];
      for (const varName of variableCandidates) {
        const normalizedVarName = String(varName || '').trim();
        if (!normalizedVarName || seen.has(normalizedVarName)) {
          continue;
        }
        seen.add(normalizedVarName);
        options.push({
          value: normalizedVarName,
          label: `${normalizedVarName} (${nodeLabel})`,
          score,
        });
      }
    }

    return options
      .sort((a, b) => {
        if (a.score !== b.score) {
          return a.score - b.score;
        }
        return a.label.localeCompare(b.label);
      })
      .map(({ value, label }) => ({ value, label }));
  }, [selectedNode?.id, nodesInCurrentPath, nodes, edges]);

  const currentDealIdTemplate = String(config.dealId ?? '{{found_deal_id}}').trim();
  const selectedDealIdVariable = dealIdVariableOptions.find(
    (option) => currentDealIdTemplate === `{{${option.value}}}`,
  )?.value || '';

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-200 leading-relaxed">
        <strong className="text-blue-800 dark:text-blue-100">Mover Etapa/Pipeline:</strong> muda a
        etapa/pipeline de um <strong>Negócio</strong> ou o <strong>status</strong> de um <strong>Lead</strong> no Bitrix24.
        <br />
        Use o ID retornado pelo nó <strong className="text-blue-800 dark:text-blue-100">Buscar Cliente</strong>
        {' '}(ex.: <strong className="text-blue-800 dark:text-blue-100">{'{{found_deal_id}}'}</strong> para negócio,
        {' '}<strong className="text-blue-800 dark:text-blue-100">{'{{lead_id}}'}</strong> para lead).
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
          Entidade
        </label>
        <select
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
          value={targetEntity}
          onChange={(e) => onChange('targetEntity', e.target.value === 'lead' ? 'lead' : 'deal')}
        >
          <option value="deal">Negócio (pipeline + etapa)</option>
          <option value="lead">Lead (status)</option>
        </select>
      </div>

      {targetEntity === 'deal' && (
        <>
      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
          Variável do ID do negócio (mesmo caminho)
        </label>
        <select
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
          value={selectedDealIdVariable}
          onChange={(e) => onChange('dealId', e.target.value ? `{{${e.target.value}}}` : '{{found_deal_id}}')}
        >
          <option value="">found_deal_id (padrão)</option>
          {dealIdVariableOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-300">
          A lista considera apenas nós <strong>Buscar Cliente</strong> que estejam no mesmo caminho acima deste nó.
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
          Pipeline de destino
        </label>
        <select
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
          value={pipelineId}
          onChange={(e) => onChange('pipelineId', e.target.value)}
        >
          <option value="current">Manter pipeline atual</option>
          <option value="all">Todos (somente etapa)</option>
          {dealCategories.map((category: any) => {
            const id = String(category?.ID ?? '').trim();
            if (!id) {
              return null;
            }

            return (
              <option key={id} value={id}>
                {category?.NAME || `Pipeline ${id}`}
              </option>
            );
          })}
        </select>
      </div>

      <div className="space-y-2">
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
            Etapa de destino
          </label>
          <select
            className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
            value={stageOptions.some((option) => option.value === stageId) ? stageId : ''}
            onChange={(e) => onChange('stageId', e.target.value)}
          >
            <option value="">Selecione uma etapa</option>
            {stageOptions.map((option) => (
              <option key={`${option.value}:${option.label}`} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <Input
          label="ID da etapa (aceita variável)"
          placeholder="Ex: NEW ou C1:WON"
          value={config.stageId || ''}
          onChange={(e) => onChange('stageId', e.target.value)}
        />

        {stageOptions.length === 0 && (
          <p className="text-[11px] text-amber-600 dark:text-amber-400">
            Não foi possível carregar as etapas da conexão. Informe o ID manualmente.
          </p>
        )}
      </div>

      <p className="text-[11px] text-slate-500 dark:text-slate-300">
        ID movido disponível automaticamente em <code>{'{{moved_deal_id}}'}</code>.
      </p>
        </>
      )}

      {targetEntity === 'lead' && (
        <>
          <Input
            label="Variável do ID do lead (aceita variável)"
            placeholder="Ex: {{lead_id}}"
            value={config.leadId ?? ''}
            onChange={(e) => onChange('leadId', e.target.value)}
          />

          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
              Status de destino
            </label>
            <select
              className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
              value={leadStatusOptions.some((option) => option.value === String(config.statusId || '')) ? String(config.statusId || '') : ''}
              onChange={(e) => onChange('statusId', e.target.value)}
            >
              <option value="">Selecione um status</option>
              {leadStatusOptions.map((option) => (
                <option key={`${option.value}:${option.label}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <Input
            label="ID do status (aceita variável)"
            placeholder="Ex: IN_PROCESS, CONVERTED, JUNK"
            value={config.statusId || ''}
            onChange={(e) => onChange('statusId', e.target.value)}
          />

          {leadStatusOptions.length === 0 && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400">
              Não foi possível carregar os status de lead da conexão. Informe o ID manualmente.
            </p>
          )}

          <p className="text-[11px] text-slate-500 dark:text-slate-300">
            ID movido disponível automaticamente em <code>{'{{moved_lead_id}}'}</code>.
          </p>
        </>
      )}

      <Input
        label={targetEntity === 'lead' ? 'Variável para o lead atualizado (opcional)' : 'Variável para o negócio atualizado (opcional)'}
        placeholder={targetEntity === 'lead' ? 'Ex: moved_lead_data' : 'Ex: moved_deal_data'}
        value={config.responseVariable || ''}
        onChange={(e) => onChange('responseVariable', e.target.value.replace(/\s/g, '_'))}
      />
    </div>
  );
};
